import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * ADA remediation (Oct 2026):
 * - When the drawer opens, focus moves to the "Cart, N items" heading so screen readers
 *   announce that the cart opened and what is in it (complaint item c).
 * - After an add-to-cart that auto-opens the drawer, "Added to cart" + item count is
 *   announced once the drawer is actually open. Previously the announcement ran before the
 *   dialog finished opening and was silently skipped.
 * - Close buttons are named "Close cart" instead of the generic "Close dialog".
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {string | undefined} */
  #pendingMessage = undefined;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleOpenAccessibility);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#labelDrawer();

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleOpenAccessibility);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart update events (Horizon dispatches adds and other updates under the same name).
   * Opens the drawer if auto-open, then announces once the drawer is actually open.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { didError?: boolean } }>} event
   */
  #handleCartAdd = (event) => {
    const count = event.detail?.resource?.item_count;
    const wasAdded = event instanceof CartAddEvent && !event.detail?.data?.didError;
    const message = wasAdded ? this.#addedMessage(count) : this.#countMessage(count);

    if (this.hasAttribute('auto-open') && !this.refs.dialog?.open) {
      // showDialog() opens on the next animation frame; announce after DialogOpenEvent.
      this.#pendingMessage = message;
      this.showDialog();
      return;
    }

    this.#announce(message);
    // Drawer contents are re-rendered after the update; re-apply labels once that lands.
    setTimeout(() => this.#labelDrawer(), 400);
  };

  /**
   * Runs every time the drawer opens: move focus to the heading and flush any pending
   * "added to cart" announcement.
   */
  #handleOpenAccessibility = () => {
    const { dialog } = this.refs;
    if (!dialog) return;

    this.#labelDrawer();

    const heading = dialog.querySelector('.cart-drawer__heading');
    if (heading instanceof HTMLElement) {
      heading.focus({ preventScroll: true });
    }

    if (this.#pendingMessage !== undefined) {
      const message = this.#pendingMessage;
      this.#pendingMessage = undefined;
      // Small delay so the heading is read first and the live region change is not dropped.
      setTimeout(() => this.#announce(message), 250);
    }
  };

  /**
   * Gives the drawer heading a name that includes the item count ("Cart, 2 items") and the
   * close buttons a specific name ("Close cart"). Re-applied on open and after cart updates,
   * because the drawer contents are re-rendered from the server.
   */
  #labelDrawer() {
    const { dialog } = this.refs;
    if (!dialog) return;

    const heading = dialog.querySelector('.cart-drawer__heading');
    if (heading instanceof HTMLElement) {
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      if (!heading.classList.contains('cart-drawer__heading--empty')) {
        const countText = heading.querySelector('.cart-bubble__text-count')?.textContent?.trim() ?? '';
        const count = parseInt(countText, 10);
        const title = (heading.firstChild?.textContent || '').trim() || 'Cart';
        heading.setAttribute('aria-label', Number.isNaN(count) ? title : `${title}, ${count} ${count === 1 ? 'item' : 'items'}`);
      }
    }

    dialog.querySelectorAll('.cart-drawer__close-button').forEach((button) => {
      button.setAttribute('aria-label', 'Close cart');
      button.querySelector('.svg-wrapper')?.setAttribute('aria-hidden', 'true');
    });
  }

  /**
   * @param {number | undefined} cartCount
   * @returns {string}
   */
  #addedMessage(cartCount) {
    const added = (window.Theme && Theme.translations && Theme.translations.added_to_cart) || 'Added to cart';
    const count = this.#countMessage(cartCount);
    return count ? `${added}. ${count}` : added;
  }

  /**
   * @param {number | undefined} cartCount
   * @returns {string}
   */
  #countMessage(cartCount) {
    if (cartCount === undefined) return '';
    const countLabel = (window.Theme && Theme.translations && Theme.translations.cart_count) || 'Cart count';
    return `${countLabel}: ${cartCount}`;
  }

  /**
   * Writes a message to the drawer's live region (only meaningful while the drawer is open).
   * @param {string} message
   */
  #announce(message) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || !message) return;

    liveRegion.textContent = '';
    requestAnimationFrame(() => {
      liveRegion.textContent = message;
    });
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
